import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const TheHousePage = () => {
  const principles = [
    { title: 'Enterprise before optics', desc: 'Value should be created through useful work, not through appearances.' },
    { title: 'Ownership with responsibility', desc: 'Control carries an obligation to govern, protect and improve.' },
    { title: 'Evidence over hype', desc: 'Commercial and investment decisions should be supported by facts, capability and clear execution.' },
    { title: 'Long-term capital', desc: 'Decisions should be capable of surviving market cycles, short-term pressure and changes in fashion.' },
    { title: 'Retain what matters', desc: 'Intellectual property, strategic capability and control should not be surrendered without a compelling reason.' },
    { title: 'Build for continuity', desc: 'Companies and assets should be structured to operate beyond the immediate involvement of their founder.' },
  ];

  const activities = [
    { title: 'Ownership and governance', desc: 'Hold and govern the family\'s commercial interests within a clear ownership structure.' },
    { title: 'Enterprise development', desc: 'Support the creation, development and responsible scaling of operating companies.' },
    { title: 'Capital allocation', desc: 'Reinvest capital across businesses, intellectual property, property and selected strategic assets.' },
    { title: 'Intellectual-property stewardship', desc: 'Protect, organise and develop the intellectual property created across the portfolio.' },
    { title: 'Long-term assets', desc: 'Acquire and improve assets capable of delivering practical, strategic or generational value.' },
    { title: 'Responsible stewardship', desc: 'Support animal welfare, land stewardship, education and practical community impact.' },
  ];

  return (
    <Layout>
      <SEO
        title="About Sandend House | The Knox Family Office"
        description="Learn why Calum Knox established Sandend House as a permanent ownership and stewardship structure for businesses, intellectual property and long-term assets."
        path="/the-house"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Sandend House",
          "url": "https://theknox.co.uk/the-house",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" },
          "mainEntity": { "@id": "https://theknox.co.uk/#organization" }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="eyebrow mb-4">The House</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-8" style={{ maxWidth: '600px' }} data-testid="house-heading">A first-generation family office.</h1>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] text-lg leading-relaxed mb-4">
              Sandend House was established by Calum Knox to provide a permanent ownership and stewardship structure around the businesses, intellectual property and assets created through enterprise.
            </p>
            <p className="text-[var(--sh-grey)] text-lg leading-relaxed">
              It is designed to protect what has been built, provide discipline around future investment and hold assets against a generational horizon.
            </p>
          </div>
        </div>
      </section>

      {/* Sandend Bay Image */}
      <section className="relative border-b border-[var(--sh-rule-light)]" data-testid="house-image-section">
        <div className="aspect-[21/9] sm:aspect-[3/1] lg:aspect-[3.5/1] w-full overflow-hidden bg-[var(--sh-ink)]">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/b854yhj2_IMG_0580.webp"
            alt="Sandend Bay — wide beach and dramatic sky on Scotland's north-east coast"
            className="w-full h-full object-cover"
            data-testid="house-hero-image"
          />
        </div>
      </section>

      {/* Why Sandend House */}
      <section className="py-20 lg:py-28 bg-white border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <h2 className="font-serif text-3xl lg:text-4xl mb-8">A name grounded in place.</h2>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-4">
              Sandend House takes its name from Sandend on Scotland's north-east coast, a place of deep personal significance to Calum Knox.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed">
              The name provides a fixed point for the House: grounded in Scotland, shaped by enterprise and built with permanence in mind.
            </p>
          </div>
        </div>
      </section>

      {/* What Sandend House Does */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">One structure. A long-term purpose.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {activities.map((a, i) => (
              <div key={i} data-testid={`activity-${i}`}>
                <h3 className="font-serif text-lg mb-3 text-[var(--sh-ink)]">{a.title}</h3>
                <p className="text-[var(--sh-grey)] text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sandend House Is Not */}
      <section className="py-16 lg:py-20 bg-[var(--sh-stone)]/30 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] leading-relaxed">
              Sandend House is a proprietary single-family office.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed mt-3">
              It does not manage external client money, provide retail financial advice or offer wealth-management services to the public.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 lg:py-28">
        <div className="section-container">
          <h2 className="font-serif text-3xl lg:text-4xl mb-12">How the House thinks.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {principles.map((p, i) => (
              <div key={i} data-testid={`principle-${i}`}>
                <h3 className="font-serif text-lg mb-3 text-[var(--sh-ink)]">{p.title}</h3>
                <div className="w-8 h-px bg-[var(--sh-brass)] mb-3"></div>
                <p className="text-[var(--sh-grey)] text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 border-t border-[var(--sh-rule-light)]">
        <div className="section-container flex flex-wrap gap-4">
          <Link to="/founder" className="btn-primary" data-testid="house-cta-founder">
            Meet the Founder <ArrowRight size={16} className="ml-2" />
          </Link>
          <Link to="/portfolio" className="btn-secondary" data-testid="house-cta-portfolio">
            Explore the Portfolio <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default TheHousePage;
